import { Check, Minus } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import {
  Checkbox as AriaCheckbox,
  type CheckboxProps as AriaCheckboxProps,
} from 'react-aria-components';

export type CheckboxSize = 'sm' | 'md';

export interface CheckboxProps extends Omit<AriaCheckboxProps, 'children' | 'className' | 'style'> {
  children?: ReactNode;
  size?: CheckboxSize;
  className?: string;
  style?: CSSProperties;
}

const SIZE: Record<CheckboxSize, { box: number; icon: number; label: string; gap: number }> = {
  sm: { box: 18, icon: 12, label: 'var(--text-xs)', gap: 8 },
  md: { box: 22, icon: 14, label: 'var(--text-sm)', gap: 10 },
};

export function Checkbox({ children, size = 'sm', className, style, ...props }: CheckboxProps) {
  const config = SIZE[size];

  return (
    <AriaCheckbox
      {...props}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: config.gap,
        cursor: props.isDisabled ? 'not-allowed' : 'pointer',
        fontFamily: 'var(--font-sans)',
        opacity: props.isDisabled ? 0.55 : 1,
        ...style,
      }}
    >
      {({ isSelected, isIndeterminate, isHovered, isFocusVisible }) => {
        const active = isSelected || isIndeterminate;

        return (
          <>
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: config.box,
                height: config.box,
                flexShrink: 0,
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-text-on-accent)',
                background: active ? 'var(--color-accent)' : 'var(--color-surface-solid)',
                border: active
                  ? '1.5px solid var(--color-accent)'
                  : `1.5px solid ${
                      isHovered ? 'var(--color-border-strong)' : 'var(--color-surface-border)'
                    }`,
                boxShadow: isFocusVisible
                  ? '0 0 0 3px var(--color-accent-outline)'
                  : 'var(--control-shadow-subtle)',
                transition:
                  'background var(--motion-fast) var(--motion-ease-out), border-color var(--motion-fast) var(--motion-ease-out), box-shadow var(--motion-fast) var(--motion-ease-out)',
              }}
            >
              {isSelected && <Check size={config.icon} strokeWidth={3} />}
              {isIndeterminate && <Minus size={config.icon} strokeWidth={3} />}
            </span>
            {children ? (
              <span
                style={{
                  color: 'var(--color-text-secondary)',
                  fontSize: config.label,
                  fontWeight: 'var(--weight-medium)',
                  lineHeight: 'var(--leading-normal)',
                }}
              >
                {children}
              </span>
            ) : null}
          </>
        );
      }}
    </AriaCheckbox>
  );
}
